import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, ClipboardList, CalendarCheck2, Palmtree, Trophy, Briefcase, Users, UserCog, LogOut, Moon, Sun, CalendarClock, Menu, X, WifiOff, PhoneCall } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { BRAND } from '@/lib/brand';
import NotificationsBell from '@/components/NotificationsBell';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'hr'], testid: 'nav-dashboard' },
  { to: '/report', label: 'Daily Report', icon: ClipboardList, roles: ['hr', 'manager', 'admin'], testid: 'nav-report' },
  { to: '/attendance', label: 'Attendance', icon: CalendarCheck2, roles: ['admin', 'manager', 'hr'], testid: 'nav-attendance' },
  { to: '/leaves', label: 'Leaves', icon: Palmtree, roles: ['admin', 'manager', 'hr'], testid: 'nav-leaves' },
  { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, roles: ['admin', 'manager', 'hr'], testid: 'nav-leaderboard' },
  { to: '/jobs', label: 'Job Openings', icon: Briefcase, roles: ['admin', 'manager', 'hr'], testid: 'nav-jobs' },
  { to: '/lineups', label: 'Lineups', icon: Users, roles: ['admin', 'manager', 'hr'], testid: 'nav-lineups' },
  { to: '/call-logging', label: 'Call Logging', icon: PhoneCall, roles: ['admin', 'manager', 'hr'], testid: 'nav-call-logging' },
  { to: '/interviews', label: 'Interviews', icon: CalendarClock, roles: ['admin', 'manager', 'hr'], testid: 'nav-interviews' },
  { to: '/team', label: 'Team', icon: UserCog, roles: ['admin'], testid: 'nav-team' },
  { to: '/offline-log', label: 'Offline Log', icon: WifiOff, roles: ['admin'], testid: 'nav-offline-log' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [dark, setDark] = useState(false);
  // Sidebar open state — persisted per-device
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem('rh_sidebar');
    if (saved !== null) return saved === '1';
    return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
  });

  useEffect(() => {
    const isDark = localStorage.getItem('rh_theme') === 'dark';
    setDark(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);
  const toggleTheme = () => {
    const next = !dark; setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('rh_theme', next ? 'dark' : 'light');
  };

  const toggleSidebar = () => {
    setOpen(o => { localStorage.setItem('rh_sidebar', !o ? '1' : '0'); return !o; });
  };

  const initials = (user?.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const roleColor = user?.role === 'admin' ? 'text-primary' : user?.role === 'manager' ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="min-h-screen bg-background relative">
      {/* Overlay for mobile when sidebar open */}
      {open && (
        <button
          aria-label="Close sidebar"
          data-testid="sidebar-overlay"
          onClick={toggleSidebar}
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
        />
      )}

      {/* Sidebar — slides in/out */}
      <aside
        data-testid="app-sidebar"
        data-open={open}
        className={`fixed z-40 top-0 left-0 h-screen w-64 bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-fg))] flex flex-col border-r border-zinc-800 transform transition-transform duration-300 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt={BRAND.short} className="w-9 h-9 rounded-md bg-white p-1 object-contain" />
            <div>
              <div className="font-display text-lg font-black tracking-tight leading-none">{BRAND.short}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{BRAND.full}</div>
            </div>
          </div>
          <button onClick={toggleSidebar} data-testid="sidebar-close" className="text-zinc-400 hover:text-white lg:hidden">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.filter(n => n.roles.includes(user?.role)).map(n => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} data-testid={n.testid}
              onClick={() => { if (window.innerWidth < 1024) toggleSidebar(); }}
              className={({ isActive }) => `sidebar-link ${isActive ? 'sidebar-link-active' : ''}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800 space-y-2">
          <div className="flex items-center gap-3 p-2 rounded-md">
            <Avatar className="w-9 h-9 border border-zinc-700">
              <AvatarFallback className="bg-primary/20 text-white font-display">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className={`text-[10px] uppercase tracking-widest ${roleColor}`}>{user?.role}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={toggleTheme} data-testid="theme-toggle" className="flex-1 text-zinc-300 hover:text-white hover:bg-white/5">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={() => { logout(); nav('/login'); }} className="flex-1 text-zinc-300 hover:text-white hover:bg-white/5">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className={`min-h-screen transition-[padding] duration-300 ${open ? 'lg:pl-64' : 'pl-0'}`}>
        {/* Top bar with hamburger */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b border-border/70 flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={toggleSidebar} data-testid="sidebar-toggle" aria-label="Toggle sidebar">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src={BRAND.logoUrl} alt="" className="w-6 h-6 rounded-sm bg-white p-0.5 object-contain" />
            <span className="font-display font-black tracking-tight">{BRAND.short}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">· {BRAND.full}</span>
          </div>
          <NotificationsBell role={user?.role} />
        </header>
        <main className="min-w-0"><Outlet /></main>
      </div>
    </div>
  );
}
